import { Role } from 'API';
import { RootState } from 'app/store/rootReducer';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { setColumnFilters, setCodingType } from 'app/store/filtering/searchAndFilterSlice';
import { getTransactionsWithFilters, selectTransactionsSlice } from 'app/pages/store/transactionsSlice';
import { generateTransactionFilterObject, getFilterForPage } from 'util/filtering/functions';
import {
  ApplyDateFilterPayload,
  TransactionsTableFilters,
  ApplySortFilterPayload,
  ApplyColumnFiltersPayload,
  ClearColumnFiltersPayload,
  ExecuteLoadMore,
} from 'util/filtering/models';

export const applyTransactionDateFilter = createAsyncThunk<any, ApplyDateFilterPayload>(
  'searchAndFilter/transactions/applyDateFilter',
  async ({ pageName }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const searchAndFilter = state.searchAndFilter[pageName] as TransactionsTableFilters | undefined;
    const filter = getFilterForPage(state, pageName);
    const response = await dispatch(
      getTransactionsWithFilters({
        filterObject: filter,
        sortObject: searchAndFilter?.sortOption?.field ? searchAndFilter?.sortOption : undefined,
        ...(searchAndFilter?.pageSize && { limit: searchAndFilter.pageSize }),
      })
    );
    return response;
  }
);

export const applySortFilter = createAsyncThunk<any, ApplySortFilterPayload>(
  'searchAndFilter/transactions/applySortFilter',
  async ({ pageName, sortOption }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const searchAndFilter = state.searchAndFilter[pageName] as TransactionsTableFilters | undefined;
    const filter = getFilterForPage(state, pageName);
    const response = await dispatch(
      getTransactionsWithFilters({
        filterObject: filter,
        sortObject: sortOption.field ? sortOption : undefined,
        ...(searchAndFilter?.pageSize && { limit: searchAndFilter.pageSize }),
      })
    );
    return response;
  }
);

export const applyTransactionColumnFilters = createAsyncThunk<any, ApplyColumnFiltersPayload>(
  'searchAndFilter/transactions/applyColumnFilters',
  async ({ pageName, columnFilters }, { dispatch, getState }) => {
    const state = getState() as RootState;

    dispatch(setColumnFilters({ pageName, columnFilters }));

    const searchAndFilter = state.searchAndFilter[pageName] as TransactionsTableFilters | undefined;
    const filter = generateTransactionFilterObject(
      {
        ...searchAndFilter,
        columnFilters: columnFilters,
        filtering: searchAndFilter?.filtering || false,
      },
      state.userCompanies.selectedPaidolId,
      state.userCompanies.userRole || Role.CARDHOLDER,
      state.userCompanies.highnotePaymentCardIds
    );

    const response = await dispatch(
      getTransactionsWithFilters({
        filterObject: filter,
        sortObject: searchAndFilter?.sortOption?.field ? searchAndFilter?.sortOption : undefined,
        ...(searchAndFilter?.pageSize && { limit: searchAndFilter.pageSize }),
      })
    );
    return response;
  }
);

// SEE-974: apply the table "Transaction type" quick filter (All/GL/Project/
// Equipment) and re-run the search. Stores the pick in searchAndFilter[pageName]
// so getFilterForPage picks it up on this and every subsequent fetch.
export const applyCodingTypeFilter = createAsyncThunk<any, { pageName: string; codingType: string }>(
  'searchAndFilter/transactions/applyCodingType',
  async ({ pageName, codingType }, { dispatch, getState }) => {
    dispatch(setCodingType({ pageName, codingType }));

    const state = getState() as RootState;
    const searchAndFilter = state.searchAndFilter[pageName] as TransactionsTableFilters | undefined;
    const response = await dispatch(
      getTransactionsWithFilters({
        filterObject: getFilterForPage(state, pageName),
        sortObject: searchAndFilter?.sortOption?.field ? searchAndFilter?.sortOption : undefined,
        ...(searchAndFilter?.pageSize && { limit: searchAndFilter.pageSize }),
      })
    );
    return response;
  }
);

export const clearTransactionColumnFilters = createAsyncThunk<any, ClearColumnFiltersPayload>(
  'searchAndFilter/transactions/clearColumnFilters',
  async ({ pageName }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const searchAndFilter = state.searchAndFilter[pageName] as TransactionsTableFilters | undefined;
    const response = await dispatch(
      getTransactionsWithFilters({
        filterObject: generateTransactionFilterObject(
          { ...searchAndFilter, columnFilters: {}, filtering: searchAndFilter?.filtering ?? false },
          state.userCompanies.selectedPaidolId,
          state.userCompanies.userRole || Role.CARDHOLDER,
          state.userCompanies.highnotePaymentCardIds
        ),
        ...(searchAndFilter?.pageSize && { limit: searchAndFilter.pageSize }),
      })
    );
    return response;
  }
);

export const executeTransactionLoadMore = createAsyncThunk<any, ExecuteLoadMore>(
  'searchAndFilter/transactions/executeLoadMore',
  async ({ pageName, nextToken, limit }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const searchAndFilter = state.searchAndFilter[pageName];
    const filter = getFilterForPage(state, pageName);
    const response = await dispatch(
      getTransactionsWithFilters({
        filterObject: filter,
        sortObject: searchAndFilter?.sortOption?.field ? searchAndFilter?.sortOption : undefined,
        nextToken,
        ...(limit && { limit }),
      })
    );
    return response;
  },
  {
    // Drop the dispatch if a load-more is already in flight. The inner
    // getTransactionsWithFilters.pending reducer flips loadingMore=true synchronously,
    // so any concurrent load-more triggered by a rapid pagination click bails here
    // instead of producing a duplicate request with a stale nextToken (SEE-780).
    condition: (_arg, { getState }) => {
      const state = getState() as RootState;
      return !selectTransactionsSlice(state).loadingMore;
    },
  }
);

export const executeTransactionSearch = createAsyncThunk<any, { pageName: string; limit?: number }>(
  'searchAndFilter/transactions/executeSearch',
  async ({ pageName, limit }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const searchAndFilter = state.searchAndFilter[pageName] as TransactionsTableFilters | undefined;
    const filter = getFilterForPage(state, pageName);
    const response = await dispatch(
      getTransactionsWithFilters({
        filterObject: filter,
        sortObject: searchAndFilter?.sortOption?.field ? searchAndFilter?.sortOption : undefined,
        limit: limit ?? searchAndFilter?.pageSize,
      })
    );
    return response;
  }
);
